// Ticket 25: Gift Animations Hook & Provider
import React, { createContext, useCallback, useContext, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { GiftAnimation } from "../components/GiftAnimation";

const GiftAnimationContext = createContext(null);

export function GiftAnimationProvider({ children }) {
  const [animations, setAnimations] = useState([]);

  // Remove finished animation
  const removeAnimation = useCallback((id) => {
    setAnimations((prev) => prev.filter((a) => a.id !== id));
  }, []);

  // Public function to trigger an animation
  const triggerGiftAnimation = useCallback((payload) => {
    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setAnimations((prev) => [
      ...prev,
      {
        id,
        type: payload.animation || payload.gift_key || payload.giftId || "generic",
        giftId: payload.giftId || payload.gift_key,
        giftName: payload.giftName || payload.gift_name,
        giftIcon: payload.giftIcon || payload.gift_emoji,
        fromUserName: payload.fromUserName || payload.from_user_name,
        toUserName: payload.toUserName || payload.to_user_name,
      },
    ]);
  }, []);

  // Create a portal target
  const [portalEl, setPortalEl] = useState(null);

  useEffect(() => {
    let el = document.getElementById("gift-animation-root");
    if (!el) {
      el = document.createElement("div");
      el.id = "gift-animation-root";
      document.body.appendChild(el);
    }
    setPortalEl(el);
    
    return () => {
      // Cleanup on unmount
      if (el && el.parentNode) {
        el.parentNode.removeChild(el);
      }
    };
  }, []);

  return (
    <GiftAnimationContext.Provider value={{ triggerGiftAnimation }}>
      {children}
      {portalEl &&
        createPortal(
          <div className="pointer-events-none fixed inset-0 z-[60] flex items-center justify-center">
            {animations.map((a) => (
              <GiftAnimation
                key={a.id}
                animation={a}
                onDone={() => removeAnimation(a.id)}
              />
            ))}
          </div>,
          portalEl
        )}
    </GiftAnimationContext.Provider>
  );
}

export function useGiftAnimations() {
  const ctx = useContext(GiftAnimationContext);
  if (!ctx) {
    throw new Error("useGiftAnimations must be used within GiftAnimationProvider");
  }
  return ctx;
}
